/**
 * Structured content blocks for funding appeal landing pages. The AI agent emits this shape
 * (small, content-only) instead of full HTML — `renderLandingPageHtmlFromBlocks` turns it into
 * a self-contained, mobile-friendly HTML document for the public landing page renderer.
 *
 * Keep block types minimal and additive. Page-level colors go in `pageStyle`; per-section
 * overrides go in optional `style` on each block. Default `template` is `appeal` (email-like
 * card + accent header band). Only fall back to the raw HTML tool for layouts blocks cannot express.
 */

import { parse } from "node-html-parser";
import { formatCurrencyCents } from "./format";
import {
  FUNDING_APPEAL_CONTACT_FORM_CSS,
  renderFundingAppealContactFormHtml,
} from "./fundingAppealContactFormMarkup";
import type { FundingDonationTier } from "./fundingDonationTiers";
import {
  FUNDING_APPEAL_CONTACT_FORM_ATTR,
  fundingAppealInboundEmailAddress,
  resolveFundingLandingPageMarkers,
} from "./fundingAppealLandingMarkers";
import {
  type FundingClubSocialLinksContext,
  renderFundingClubSocialLinksHtml,
  normalizeFundingClubSocialLinksSelection,
  resolveFundingClubSocialLinks,
} from "./fundingClubSocialLinks";

/** Optional colors on a single block (same fields as `pageStyle`). */
export type LandingBlockStyle = {
  backgroundColor?: string;
  accentColor?: string;
  textColor?: string;
};

export type LandingHeroBlock = {
  id?: string;
  type: "hero";
  title: string;
  subtitle?: string;
  /** Optional full-width image in the header band (appeal template) or hero section (minimal). */
  imageSrc?: string;
  imageAlt?: string;
  cta?: { label: string; url: string };
  style?: LandingBlockStyle;
};

export type LandingStoryBlock = {
  id?: string;
  type: "story";
  heading?: string;
  paragraphs: string[];
  style?: LandingBlockStyle;
};

export type LandingImpactBlock = {
  id?: string;
  type: "impact";
  heading?: string;
  items: Array<{ value: string; label: string; caption?: string }>;
  style?: LandingBlockStyle;
};

export type LandingQuoteBlock = {
  id?: string;
  type: "quote";
  text: string;
  attribution?: string;
  style?: LandingBlockStyle;
};

/** External link CTA only — omit for appeal contact; use `email_link` / `contact_form` instead. */
export type LandingCtaBlock = {
  id?: string;
  type: "cta";
  headline: string;
  body?: string;
  buttonLabel: string;
  buttonUrl: string;
  style?: LandingBlockStyle;
};

export type LandingImageBlock = {
  id?: string;
  type: "image";
  src: string;
  alt: string;
  caption?: string;
  style?: LandingBlockStyle;
};

export type LandingEmailLinkBlock = {
  id?: string;
  type: "email_link";
  headline?: string;
  body?: string;
  linkLabel: string;
  style?: LandingBlockStyle;
};

export type LandingContactFormBlock = {
  id?: string;
  type: "contact_form";
  headline?: string;
  body?: string;
  submitLabel?: string;
  style?: LandingBlockStyle;
};

/** Links to club directory (MixItOne logo), Facebook, and website — URLs from club settings. */
export type LandingSocialLinksBlock = {
  id?: string;
  type: "social_links";
  directory?: boolean;
  facebook?: boolean;
  website?: boolean;
  style?: LandingBlockStyle;
};

/** Suggested tiers from the appeal; heading/body/button label come from the page template. */
export type LandingDonationTiersBlock = {
  id?: string;
  type: "donation_tiers";
  heading?: string;
  body?: string;
  buttonLabel: string;
  style?: LandingBlockStyle;
};

export type LandingBlock =
  | LandingHeroBlock
  | LandingStoryBlock
  | LandingImpactBlock
  | LandingQuoteBlock
  | LandingCtaBlock
  | LandingImageBlock
  | LandingEmailLinkBlock
  | LandingContactFormBlock
  | LandingSocialLinksBlock
  | LandingDonationTiersBlock;

export const LANDING_BLOCK_TYPES: ReadonlyArray<LandingBlock["type"]> = [
  "hero",
  "story",
  "impact",
  "quote",
  "cta",
  "image",
  "email_link",
  "contact_form",
  "social_links",
  "donation_tiers",
];

/** Optional page-level colors for compose_landing_page (safe subset — no raw CSS). */
export type LandingPageStyle = LandingBlockStyle;

/** Layout scaffold for composed landing pages. `appeal` is the default (email-like card + header band). */
export const LANDING_PAGE_TEMPLATES = ["appeal", "minimal"] as const;
export type LandingPageTemplate = (typeof LANDING_PAGE_TEMPLATES)[number];
export const DEFAULT_LANDING_PAGE_TEMPLATE: LandingPageTemplate = "appeal";

export type LandingPageRenderContext = {
  appealId: string;
  inboundEmailDomain?: string;
  pageStyle?: LandingPageStyle;
  /** Defaults to `appeal` — centered card with accent header band. */
  template?: LandingPageTemplate;
  /** When true, embed a static non-interactive contact form (admin preview). */
  contactFormPreview?: boolean;
  clubSocialLinks?: FundingClubSocialLinksContext;
  /** Appeal-level suggested donation tiers (from `funding_appeals.donation_tiers`). */
  donationTiers?: FundingDonationTier[];
  currency?: string;
};

/** Scroll target for tier CTAs linking to the appeal contact form. */
export const FUNDING_APPEAL_CONTACT_SECTION_ID = "mixitone-funding-contact";

/** Validates optional `template` on compose_landing_page. */
export function validateLandingTemplate(input: unknown): LandingPageTemplate {
  if (input == null) return DEFAULT_LANDING_PAGE_TEMPLATE;
  if (typeof input !== "string" || !LANDING_PAGE_TEMPLATES.includes(input as LandingPageTemplate)) {
    throw new Error(`template must be one of: ${LANDING_PAGE_TEMPLATES.join(", ")}`);
  }
  return input as LandingPageTemplate;
}

const ALLOWED_CSS_COLOR_NAMES = new Set([
  "aliceblue",
  "antiquewhite",
  "aqua",
  "azure",
  "beige",
  "black",
  "blue",
  "brown",
  "cadetblue",
  "chartreuse",
  "chocolate",
  "coral",
  "cornflowerblue",
  "cornsilk",
  "crimson",
  "cyan",
  "darkblue",
  "darkcyan",
  "darkgoldenrod",
  "darkgray",
  "darkgreen",
  "darkgrey",
  "darkkhaki",
  "darkmagenta",
  "darkolivegreen",
  "darkorange",
  "darkorchid",
  "darkred",
  "darksalmon",
  "darkseagreen",
  "darkslateblue",
  "darkslategray",
  "darkslategrey",
  "darkturquoise",
  "darkviolet",
  "deeppink",
  "deepskyblue",
  "dimgray",
  "dimgrey",
  "dodgerblue",
  "firebrick",
  "floralwhite",
  "forestgreen",
  "fuchsia",
  "gainsboro",
  "ghostwhite",
  "gold",
  "goldenrod",
  "gray",
  "green",
  "greenyellow",
  "grey",
  "honeydew",
  "hotpink",
  "indianred",
  "indigo",
  "ivory",
  "khaki",
  "lavender",
  "lavenderblush",
  "lawngreen",
  "lemonchiffon",
  "lightblue",
  "lightcoral",
  "lightcyan",
  "lightgoldenrodyellow",
  "lightgray",
  "lightgreen",
  "lightgrey",
  "lightpink",
  "lightsalmon",
  "lightseagreen",
  "lightskyblue",
  "lightsteelblue",
  "lightyellow",
  "lime",
  "limegreen",
  "linen",
  "magenta",
  "maroon",
  "mediumaquamarine",
  "mediumblue",
  "mediumorchid",
  "mediumpurple",
  "mediumseagreen",
  "mediumslateblue",
  "mediumspringgreen",
  "mediumturquoise",
  "mediumvioletred",
  "midnightblue",
  "mintcream",
  "mistyrose",
  "moccasin",
  "navajowhite",
  "navy",
  "oldlace",
  "olive",
  "olivedrab",
  "orange",
  "orangered",
  "orchid",
  "palegoldenrod",
  "palegreen",
  "paleturquoise",
  "palevioletred",
  "papayawhip",
  "peachpuff",
  "peru",
  "pink",
  "plum",
  "powderblue",
  "purple",
  "rebeccapurple",
  "red",
  "rosybrown",
  "royalblue",
  "saddlebrown",
  "salmon",
  "sandybrown",
  "seagreen",
  "seashell",
  "sienna",
  "silver",
  "skyblue",
  "slateblue",
  "slategray",
  "slategrey",
  "snow",
  "springgreen",
  "steelblue",
  "tan",
  "teal",
  "thistle",
  "tomato",
  "turquoise",
  "violet",
  "wheat",
  "white",
  "whitesmoke",
  "yellow",
  "yellowgreen",
]);

function validateCssColor(value: unknown, fieldLabel: string): string {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(
      `${fieldLabel} must be a non-empty string (#RGB, #RRGGBB, rgb(), or a standard color name)`,
    );
  }
  const v = value.trim();
  if (/^#[0-9a-fA-F]{3}$/.test(v) || /^#[0-9a-fA-F]{6}$/.test(v)) return v;
  const rgb = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i.exec(v);
  if (rgb) {
    const parts = [rgb[1], rgb[2], rgb[3]].map((n) => Number(n));
    if (parts.every((n) => n >= 0 && n <= 255)) return v;
  }
  const name = v.toLowerCase();
  if (ALLOWED_CSS_COLOR_NAMES.has(name)) return name;
  throw new Error(
    `${fieldLabel} is not a safe color (${v}). Use #RGB, #RRGGBB, rgb(r,g,b), or a standard CSS color name.`,
  );
}

function parseLandingStyleFields(raw: Record<string, unknown>, fieldPrefix: string): LandingBlockStyle {
  const style: LandingBlockStyle = {};
  if (raw.backgroundColor != null) {
    style.backgroundColor = validateCssColor(raw.backgroundColor, `${fieldPrefix}.backgroundColor`);
  }
  if (raw.accentColor != null) {
    style.accentColor = validateCssColor(raw.accentColor, `${fieldPrefix}.accentColor`);
  }
  if (raw.textColor != null) {
    style.textColor = validateCssColor(raw.textColor, `${fieldPrefix}.textColor`);
  }
  if (!style.backgroundColor && !style.accentColor && !style.textColor) {
    throw new Error(`${fieldPrefix} must include at least one of backgroundColor, accentColor, textColor`);
  }
  return style;
}

/** Validates optional `style` on a landing page block. */
export function validateLandingBlockStyle(input: unknown, blockIndex: number): LandingBlockStyle | undefined {
  if (input == null) return undefined;
  if (typeof input !== "object") throw new Error(`blocks[${blockIndex}].style must be an object`);
  return parseLandingStyleFields(input as Record<string, unknown>, `blocks[${blockIndex}].style`);
}

/** Validates optional pageStyle on compose_landing_page. */
export function validateLandingPageStyle(input: unknown): LandingPageStyle | undefined {
  if (input == null) return undefined;
  if (typeof input !== "object") throw new Error("pageStyle must be an object");
  return parseLandingStyleFields(input as Record<string, unknown>, "pageStyle");
}

/** True when HTML would replace a full page with little or no visible content (e.g. background-only stub). */
export function landingPageHtmlLooksLikeStyleOnlyStub(html: string): boolean {
  const trimmed = html.trim();
  if (trimmed.length >= 1200) return false;
  const withoutStyles = trimmed.replace(/<style[\s\S]*?<\/style>/gi, "");
  const hasContent =
    /<h1[\s>]/i.test(withoutStyles) ||
    /<h2[\s>]/i.test(withoutStyles) ||
    /class="(hero|page-header|story|impact|quote|cta|email-link|contact-form)"/i.test(withoutStyles) ||
    (/<p[\s>]/i.test(withoutStyles) && withoutStyles.length > 400);
  return !hasContent;
}

/** Light validation. Throws on shape errors so the tool returns a clear message to the LLM. */
export function validateLandingBlocks(input: unknown): LandingBlock[] {
  if (!Array.isArray(input)) throw new Error("blocks must be an array");
  if (input.length === 0) throw new Error("blocks must contain at least one block");
  return input.map((raw, i) => validateBlock(raw, i));
}

function blockStyleFromRaw(b: Record<string, unknown>, index: number): LandingBlockStyle | undefined {
  return validateLandingBlockStyle(b.style, index);
}

function validateBlock(raw: unknown, index: number): LandingBlock {
  if (!raw || typeof raw !== "object") throw new Error(`blocks[${index}] must be an object`);
  const b = raw as Record<string, unknown>;
  const type = b.type;
  const style = blockStyleFromRaw(b, index);
  switch (type) {
    case "hero": {
      requireString(b, "title", index);
      return {
        type: "hero",
        id: optionalString(b.id),
        title: String(b.title),
        subtitle: optionalString(b.subtitle),
        imageSrc: optionalString(b.imageSrc),
        imageAlt: optionalString(b.imageAlt),
        cta: validateHeroCta(b.cta, index),
        style,
      };
    }
    case "story": {
      const paragraphs = b.paragraphs;
      if (!Array.isArray(paragraphs) || paragraphs.length === 0) {
        throw new Error(`blocks[${index}] (story) requires a non-empty paragraphs array`);
      }
      return {
        type: "story",
        id: optionalString(b.id),
        heading: optionalString(b.heading),
        paragraphs: paragraphs.map((p) => String(p)),
        style,
      };
    }
    case "impact": {
      const items = b.items;
      if (!Array.isArray(items) || items.length === 0) {
        throw new Error(`blocks[${index}] (impact) requires a non-empty items array`);
      }
      return {
        type: "impact",
        id: optionalString(b.id),
        heading: optionalString(b.heading),
        items: items.map((it, j) => {
          if (!it || typeof it !== "object")
            throw new Error(`blocks[${index}].items[${j}] must be an object`);
          const row = it as Record<string, unknown>;
          if (typeof row.value !== "string" || typeof row.label !== "string") {
            throw new Error(`blocks[${index}].items[${j}] requires string value and label`);
          }
          return { value: row.value, label: row.label, caption: optionalString(row.caption) };
        }),
        style,
      };
    }
    case "quote": {
      requireString(b, "text", index);
      return {
        type: "quote",
        id: optionalString(b.id),
        text: String(b.text),
        attribution: optionalString(b.attribution),
        style,
      };
    }
    case "cta": {
      requireString(b, "headline", index);
      requireString(b, "buttonLabel", index);
      requireString(b, "buttonUrl", index);
      return {
        type: "cta",
        id: optionalString(b.id),
        headline: String(b.headline),
        body: optionalString(b.body),
        buttonLabel: String(b.buttonLabel),
        buttonUrl: String(b.buttonUrl),
        style,
      };
    }
    case "image": {
      requireString(b, "src", index);
      requireString(b, "alt", index);
      return {
        type: "image",
        id: optionalString(b.id),
        src: String(b.src),
        alt: String(b.alt),
        caption: optionalString(b.caption),
        style,
      };
    }
    case "email_link": {
      requireString(b, "linkLabel", index);
      return {
        type: "email_link",
        id: optionalString(b.id),
        headline: optionalString(b.headline),
        body: optionalString(b.body),
        linkLabel: String(b.linkLabel),
        style,
      };
    }
    case "contact_form": {
      return {
        type: "contact_form",
        id: optionalString(b.id),
        headline: optionalString(b.headline),
        body: optionalString(b.body),
        submitLabel: optionalString(b.submitLabel),
        style,
      };
    }
    case "social_links":
      return {
        type: "social_links",
        id: optionalString(b.id),
        directory: optionalBool(b.directory, true),
        facebook: optionalBool(b.facebook, true),
        website: optionalBool(b.website, true),
        style,
      };
    case "donation_tiers": {
      requireString(b, "buttonLabel", index);
      return {
        type: "donation_tiers",
        id: optionalString(b.id),
        heading: optionalString(b.heading),
        body: optionalString(b.body),
        buttonLabel: String(b.buttonLabel),
        style,
      };
    }
    default:
      throw new Error(
        `blocks[${index}] has unknown type "${String(type)}". Allowed: ${LANDING_BLOCK_TYPES.join(", ")}`,
      );
  }
}

function validateHeroCta(raw: unknown, index: number): { label: string; url: string } | undefined {
  if (raw == null) return undefined;
  if (typeof raw !== "object") throw new Error(`blocks[${index}].cta must be an object`);
  const cta = raw as Record<string, unknown>;
  if (typeof cta.label !== "string" || !cta.label.trim()) {
    throw new Error(`blocks[${index}].cta requires a string label`);
  }
  if (typeof cta.url !== "string" || !cta.url.trim()) {
    throw new Error(`blocks[${index}].cta requires a string url (external link only)`);
  }
  return { label: cta.label, url: cta.url };
}

function requireString(b: Record<string, unknown>, key: string, index: number): void {
  if (typeof b[key] !== "string" || !(b[key] as string).trim()) {
    throw new Error(`blocks[${index}] (${String(b.type)}) requires non-empty string "${key}"`);
  }
}

function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v : undefined;
}

function optionalBool(v: unknown, defaultValue: boolean): boolean {
  if (typeof v === "boolean") return v;
  return defaultValue;
}

/* -------------------------------------------------------------------------------------------- */
/* Rendering                                                                                    */
/* -------------------------------------------------------------------------------------------- */

/** Matches `fundingEmailTemplateBlocks` — shared visual language for email + landing. */
const CANVAS_BG = "#f4f6f8";
const CARD_BG = "#ffffff";
const TEXT_COLOR = "#1f2937";
const HEADING_COLOR = "#0f172a";
const MUTED_COLOR = "#4b5563";
const ACCENT = "#1d4ed8";
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const MINIMAL_PAGE_VARS = {
  "--page-bg": CARD_BG,
  "--text": TEXT_COLOR,
  "--heading": HEADING_COLOR,
  "--muted": MUTED_COLOR,
  "--accent": ACCENT,
  "--surface": "#f9fafb",
  "--accent-surface": "#eff6ff",
} as const;

const APPEAL_PAGE_VARS = {
  "--canvas-bg": CANVAS_BG,
  "--card-bg": CARD_BG,
  "--header-bg": ACCENT,
  "--header-text": "#ffffff",
  "--header-subtitle": "rgba(255, 255, 255, 0.88)",
  "--text": TEXT_COLOR,
  "--heading": HEADING_COLOR,
  "--muted": MUTED_COLOR,
  "--accent": ACCENT,
  "--surface": "#f9fafb",
  "--accent-surface": "#eff6ff",
} as const;

function applyPageStyleVars(
  vars: Record<string, string>,
  pageStyle: LandingPageStyle | undefined,
  template: LandingPageTemplate,
): void {
  if (pageStyle?.accentColor) {
    vars["--accent"] = pageStyle.accentColor;
    vars["--accent-surface"] = `color-mix(in srgb, ${pageStyle.accentColor} 12%, #ffffff)`;
    if (template === "appeal") vars["--header-bg"] = pageStyle.accentColor;
  }
  if (pageStyle?.textColor) {
    if (template === "appeal") {
      vars["--header-text"] = pageStyle.textColor;
      vars["--header-subtitle"] = pageStyle.textColor;
    } else {
      vars["--text"] = pageStyle.textColor;
      vars["--heading"] = pageStyle.textColor;
      vars["--muted"] = pageStyle.textColor;
    }
  }
  if (pageStyle?.backgroundColor) {
    if (template === "appeal") vars["--canvas-bg"] = pageStyle.backgroundColor;
    else vars["--page-bg"] = pageStyle.backgroundColor;
  }
}

function buildMinimalLandingPageStyles(pageStyle: LandingPageStyle | undefined): string {
  const vars: Record<string, string> = { ...MINIMAL_PAGE_VARS };
  applyPageStyleVars(vars, pageStyle, "minimal");
  const rootDecl = Object.entries(vars)
    .map(([k, v]) => `${k}: ${v}`)
    .join("; ");
  return `
  :root { ${rootDecl}; }
  body { margin: 0; font-family: ${FONT}; color: var(--text); line-height: 1.55; background: var(--page-bg); }
  .page { max-width: 720px; margin: 0 auto; padding: 24px 20px 48px; min-height: 100vh; }
  section { margin: 0 0 32px; }
  h1, h2, h3 { color: var(--heading); line-height: 1.2; margin: 0 0 12px; }
  h1 { font-size: 32px; }
  h2 { font-size: 24px; }
  h3 { font-size: 18px; }
  p { margin: 0 0 12px; font-size: 16px; }
  a.button { display: inline-block; padding: 12px 22px; background: var(--accent); color: #ffffff; text-decoration: none; border-radius: 8px; font-weight: 600; }
  .hero { padding: 24px 0 8px; }
  .hero .hero-image { display: block; width: 100%; max-height: 280px; object-fit: cover; border-radius: 8px; margin: 0 0 20px; }
  .hero .subtitle { font-size: 18px; color: var(--muted); margin: 0 0 16px; }
  .impact { display: flex; flex-wrap: wrap; gap: 16px; padding: 16px 0; }
  .impact .item { flex: 1 1 160px; padding: 16px; background: var(--surface); border-radius: 8px; text-align: center; }
  .impact .item .value { font-size: 28px; font-weight: 700; color: var(--accent); display: block; }
  .impact .item .label { font-size: 14px; color: var(--text); display: block; margin-top: 4px; }
  .impact .item .caption { font-size: 12px; color: var(--muted); display: block; margin-top: 4px; }
  blockquote { margin: 0; padding: 16px 20px; border-left: 4px solid var(--accent); background: var(--surface); font-style: italic; color: var(--text); }
  blockquote cite { display: block; margin-top: 8px; font-style: normal; font-weight: 600; color: var(--muted); }
  figure { margin: 0; }
  figure img { display: block; width: 100%; height: auto; border-radius: 8px; }
  figure figcaption { font-size: 14px; color: var(--muted); margin-top: 8px; text-align: center; }
  .cta, .email-link, .contact-form { padding: 24px; background: var(--accent-surface); border-radius: 12px; text-align: center; }
  .cta h2, .email-link h2, .contact-form h2 { margin-bottom: 8px; }
  .cta p, .email-link p, .contact-form p { color: var(--text); }
  .contact-form-mount { min-height: 1px; }
  .social-links { display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 12px; padding: 8px 0 16px; }
  .social-links .social-link { display: inline-flex; align-items: center; gap: 6px; font-size: 15px; font-weight: 600; color: var(--accent); text-decoration: none; }
  .social-links .social-link:hover { text-decoration: underline; }
  .social-links .social-link-directory img { display: block; border-radius: 8px; }
  .donation-tiers { padding: 8px 0 16px; }
  .donation-tiers .tier-grid { display: flex; flex-wrap: wrap; gap: 14px; justify-content: center; margin-top: 16px; }
  .donation-tiers .tier-card { flex: 1 1 180px; max-width: 220px; padding: 18px 16px; background: var(--surface); border-radius: 10px; text-align: center; border: 2px solid transparent; }
  .donation-tiers .tier-card.highlight { border-color: var(--accent); background: var(--accent-surface); }
  .donation-tiers .tier-amount { font-size: 26px; font-weight: 700; color: var(--accent); display: block; margin-bottom: 6px; }
  .donation-tiers .tier-label { font-size: 15px; font-weight: 600; color: var(--heading); display: block; margin-bottom: 6px; }
  .donation-tiers .tier-desc { font-size: 13px; color: var(--muted); margin: 0 0 12px; line-height: 1.4; }
  ${FUNDING_APPEAL_CONTACT_FORM_CSS}
`.trim();
}

function buildAppealLandingPageStyles(pageStyle: LandingPageStyle | undefined): string {
  const vars: Record<string, string> = { ...APPEAL_PAGE_VARS };
  applyPageStyleVars(vars, pageStyle, "appeal");
  const rootDecl = Object.entries(vars)
    .map(([k, v]) => `${k}: ${v}`)
    .join("; ");
  return `
  :root { ${rootDecl}; }
  body { margin: 0; font-family: ${FONT}; color: var(--text); line-height: 1.55; background: var(--canvas-bg); }
  .shell { max-width: 600px; margin: 0 auto; padding: 32px 16px 48px; min-height: 100vh; box-sizing: border-box; }
  .page.card { background: var(--card-bg); border-radius: 12px; overflow: hidden; box-shadow: 0 4px 24px rgba(15, 23, 42, 0.08); }
  .page-header { margin: 0; padding: 32px 28px 28px; background: var(--header-bg); color: var(--header-text); text-align: center; }
  .page-header.has-hero-image { padding: 0; text-align: center; }
  .page-header .hero-image-wrap { margin: 0 -28px 20px; overflow: hidden; border-radius: 12px 12px 0 0; }
  .page-header.has-hero-image .hero-image-wrap { margin: 0; border-radius: 12px 12px 0 0; }
  .page-header .hero-image { display: block; width: 100%; max-height: 280px; object-fit: cover; }
  .page-header.has-hero-image .page-header-content { padding: 28px 28px 28px; text-align: center; }
  .page-header.has-hero-image .page-header-content h1,
  .page-header.has-hero-image .page-header-content .subtitle { text-align: center; }
  .page-header h1 { color: var(--header-text); font-size: 30px; line-height: 1.15; margin: 0 0 10px; }
  .page-header .subtitle { font-size: 17px; color: var(--header-subtitle); margin: 0 0 18px; line-height: 1.45; }
  .page-header.has-hero-image .page-header-content .subtitle:last-child { margin-bottom: 0; }
  .page-header a.button { background: #ffffff; color: var(--header-bg); margin-top: 4px; }
  .page-body { padding: 28px 28px 8px; }
  .page-body section { margin: 0 0 28px; }
  .page-body section:last-child { margin-bottom: 0; }
  .page-footer { padding: 0 28px 28px; }
  .page-footer section { margin: 0 0 16px; }
  .page-footer section:last-child { margin-bottom: 0; }
  h1, h2, h3 { color: var(--heading); line-height: 1.2; margin: 0 0 12px; }
  h2 { font-size: 22px; }
  h3 { font-size: 18px; }
  p { margin: 0 0 12px; font-size: 16px; }
  a.button { display: inline-block; padding: 12px 22px; background: var(--accent); color: #ffffff; text-decoration: none; border-radius: 8px; font-weight: 600; }
  .impact { display: flex; flex-wrap: wrap; gap: 12px; }
  .impact .item { flex: 1 1 140px; padding: 14px; background: var(--surface); border-radius: 8px; text-align: center; }
  .impact .item .value { font-size: 26px; font-weight: 700; color: var(--accent); display: block; }
  .impact .item .label { font-size: 14px; color: var(--text); display: block; margin-top: 4px; }
  .impact .item .caption { font-size: 12px; color: var(--muted); display: block; margin-top: 4px; }
  blockquote { margin: 0; padding: 16px 20px; border-left: 4px solid var(--accent); background: var(--surface); font-style: italic; color: var(--text); }
  blockquote cite { display: block; margin-top: 8px; font-style: normal; font-weight: 600; color: var(--muted); }
  figure { margin: 0; }
  figure img { display: block; width: 100%; height: auto; border-radius: 8px; }
  figure figcaption { font-size: 14px; color: var(--muted); margin-top: 8px; text-align: center; }
  .page-footer .cta, .page-footer .email-link, .page-footer .contact-form { padding: 22px; background: var(--accent-surface); border-radius: 10px; text-align: center; }
  .page-body .cta { padding: 22px; background: var(--accent-surface); border-radius: 10px; text-align: center; }
  .cta h2, .email-link h2, .contact-form h2 { margin-bottom: 8px; }
  .cta p, .email-link p, .contact-form p { color: var(--text); }
  .contact-form-mount { min-height: 1px; }
  .page-footer .social-links { padding: 4px 0 8px; }
  .social-links { display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 12px; }
  .social-links .social-link { display: inline-flex; align-items: center; gap: 6px; font-size: 15px; font-weight: 600; color: var(--accent); text-decoration: none; }
  .social-links .social-link:hover { text-decoration: underline; }
  .social-links .social-link-directory img { display: block; border-radius: 8px; }
  .donation-tiers { padding: 8px 0 16px; }
  .donation-tiers .tier-grid { display: flex; flex-wrap: wrap; gap: 14px; justify-content: center; margin-top: 16px; }
  .donation-tiers .tier-card { flex: 1 1 180px; max-width: 220px; padding: 18px 16px; background: var(--surface); border-radius: 10px; text-align: center; border: 2px solid transparent; }
  .donation-tiers .tier-card.highlight { border-color: var(--accent); background: var(--accent-surface); }
  .donation-tiers .tier-amount { font-size: 26px; font-weight: 700; color: var(--accent); display: block; margin-bottom: 6px; }
  .donation-tiers .tier-label { font-size: 15px; font-weight: 600; color: var(--heading); display: block; margin-bottom: 6px; }
  .donation-tiers .tier-desc { font-size: 13px; color: var(--muted); margin: 0 0 12px; line-height: 1.4; }
  ${FUNDING_APPEAL_CONTACT_FORM_CSS}
`.trim();
}

function buildLandingPageStyles(
  pageStyle: LandingPageStyle | undefined,
  template: LandingPageTemplate,
): string {
  return template === "minimal"
    ? buildMinimalLandingPageStyles(pageStyle)
    : buildAppealLandingPageStyles(pageStyle);
}

/** Inline styles on outer canvas for email-safe rendering. */
function renderShellOpenTag(pageStyle: LandingPageStyle | undefined): string {
  if (!pageStyle?.backgroundColor) return '<div class="shell">';
  return `<div class="shell" style="background-color: ${escapeAttr(pageStyle.backgroundColor)}">`;
}

/** Inline styles on `<main class="page">` (minimal template). */
function renderPageMainOpenTag(pageStyle: LandingPageStyle | undefined): string {
  if (!pageStyle?.backgroundColor && !pageStyle?.textColor) {
    return '<main class="page">';
  }
  const decls: string[] = [];
  if (pageStyle.backgroundColor) decls.push(`background-color: ${pageStyle.backgroundColor}`);
  if (pageStyle.textColor) decls.push(`color: ${pageStyle.textColor}`);
  return `<main class="page" style="${escapeAttr(decls.join("; "))}">`;
}

/** Canvas / page background from composed CSS. */
export function extractLandingPageBackgroundColor(css: string): string | undefined {
  for (const name of ["--canvas-bg", "--page-bg"]) {
    const matches = [...css.matchAll(new RegExp(`${name}:\\s*([^;}\\s]+)`, "gi"))];
    const last = matches.at(-1)?.[1]?.trim();
    if (last) return last;
  }
  return undefined;
}

type PartitionedBlocks = {
  header: LandingHeroBlock[];
  body: LandingBlock[];
  footer: LandingBlock[];
};

function partitionLandingBlocks(blocks: LandingBlock[]): PartitionedBlocks {
  const header: LandingHeroBlock[] = [];
  const body: LandingBlock[] = [];
  const footer: LandingBlock[] = [];
  for (const b of blocks) {
    if (b.type === "hero") header.push(b);
    else if (b.type === "email_link" || b.type === "contact_form" || b.type === "social_links")
      footer.push(b);
    else body.push(b);
  }
  return { header, body, footer };
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttr(input: string): string {
  return escapeHtml(input);
}

function blockStyleDecls(style: LandingBlockStyle | undefined): string {
  if (!style) return "";
  const parts: string[] = [];
  if (style.backgroundColor) parts.push(`background-color: ${style.backgroundColor}`);
  if (style.textColor) parts.push(`color: ${style.textColor}`);
  return parts.join("; ");
}

function sectionOpen(className: string | undefined, style: LandingBlockStyle | undefined): string {
  const attrs: string[] = [];
  if (className) attrs.push(`class="${className}"`);
  const decls = blockStyleDecls(style);
  if (decls) attrs.push(`style="${escapeAttr(decls)}"`);
  return `<section ${attrs.join(" ")}>`;
}

function textColorAttr(style: LandingBlockStyle | undefined): string {
  return style?.textColor ? ` style="color: ${escapeAttr(style.textColor)}"` : "";
}

function textEl(
  tag: string,
  className: string | undefined,
  content: string,
  style: LandingBlockStyle | undefined,
): string {
  const attrs: string[] = [];
  if (className) attrs.push(`class="${className}"`);
  const color = textColorAttr(style);
  if (color) attrs.push(color.slice(1));
  const open = attrs.length ? ` ${attrs.join(" ")}` : "";
  return `<${tag}${open}>${escapeHtml(content)}</${tag}>`;
}

function paragraphsHtml(paragraphs: string[], style: LandingBlockStyle | undefined): string {
  return paragraphs.map((p) => textEl("p", undefined, p, style)).join("");
}

/** Relative luminance 0–1; unknown colors treated as mid. */
function relativeLuminanceForColor(color: string): number | null {
  const v = color.trim().toLowerCase();
  let r: number;
  let g: number;
  let b: number;
  const hex3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(v);
  if (hex3) {
    const expand = (c: string) => Number.parseInt(c + c, 16);
    r = expand(hex3[1]);
    g = expand(hex3[2]);
    b = expand(hex3[3]);
  } else {
    const hex6 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(v);
    if (hex6) {
      r = Number.parseInt(hex6[1], 16);
      g = Number.parseInt(hex6[2], 16);
      b = Number.parseInt(hex6[3], 16);
    } else {
      const rgb = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i.exec(v);
      if (!rgb) return null;
      r = Number(rgb[1]);
      g = Number(rgb[2]);
      b = Number(rgb[3]);
    }
  }
  const chan = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b);
}

function contrastIsReadable(backgroundColor: string, textColor: string): boolean {
  const lb = relativeLuminanceForColor(backgroundColor);
  const lt = relativeLuminanceForColor(textColor);
  if (lb == null || lt == null) return true;
  const lighter = Math.max(lb, lt);
  const darker = Math.min(lb, lt);
  return (lighter + 0.05) / (darker + 0.05) >= 4.5;
}

/** Header band colors with inline-safe defaults for clients that ignore stylesheet backgrounds. */
export function resolveAppealHeaderStyle(
  pageStyle: LandingPageStyle | undefined,
  blockStyle: LandingBlockStyle | undefined,
): { backgroundColor: string; color: string } {
  let backgroundColor = blockStyle?.backgroundColor ?? pageStyle?.accentColor ?? ACCENT;
  let color = blockStyle?.textColor ?? pageStyle?.textColor ?? "#ffffff";
  if (!contrastIsReadable(backgroundColor, color)) {
    const accent = pageStyle?.accentColor ?? ACCENT;
    backgroundColor = contrastIsReadable(accent, color) ? accent : ACCENT;
    if (!contrastIsReadable(backgroundColor, color)) color = "#ffffff";
    if (!contrastIsReadable(backgroundColor, color)) {
      backgroundColor = ACCENT;
      color = "#ffffff";
    }
  }
  return { backgroundColor, color };
}

function renderHeroImageMarkup(imageSrc: string, imageAlt: string | undefined, wrapClass: string): string {
  const alt = imageAlt?.trim() || "";
  return `<div class="${wrapClass}"><img class="hero-image" src="${escapeAttr(imageSrc)}" alt="${escapeAttr(alt)}" /></div>`;
}

function renderHeroAppeal(b: LandingHeroBlock, ctx: LandingPageRenderContext): string {
  const s = b.style;
  const header = resolveAppealHeaderStyle(ctx.pageStyle, s);
  const decls = `background-color: ${header.backgroundColor}; color: ${header.color}`;
  const textAttr = ` style="color: ${escapeAttr(header.color)}"`;
  const image = b.imageSrc?.trim()
    ? renderHeroImageMarkup(b.imageSrc.trim(), b.imageAlt, "hero-image-wrap")
    : "";
  const hasImage = image.length > 0;
  const subtitle = b.subtitle ? `<p class="subtitle"${textAttr}>${escapeHtml(b.subtitle)}</p>` : "";
  const cta = b.cta ? renderExternalButton(b.cta.label, b.cta.url, s) : "";
  const headerClass = hasImage ? "page-header hero has-hero-image" : "page-header hero";
  const heading = `<h1${textAttr}>${escapeHtml(b.title)}</h1>${subtitle}${cta}`;
  const body = hasImage
    ? `<div class="page-header-content" style="text-align: center">${heading}</div>`
    : heading;
  return `<header class="${headerClass}" style="${escapeAttr(decls)}">${image}${body}</header>`;
}

function renderHero(b: LandingHeroBlock, ctx: LandingPageRenderContext): string {
  if ((ctx.template ?? DEFAULT_LANDING_PAGE_TEMPLATE) === "appeal") return renderHeroAppeal(b, ctx);
  const s = b.style;
  const image = b.imageSrc?.trim()
    ? `<img class="hero-image" src="${escapeAttr(b.imageSrc.trim())}" alt="${escapeAttr(b.imageAlt?.trim() || "")}" />`
    : "";
  const subtitle = b.subtitle ? textEl("p", "subtitle", b.subtitle, s) : "";
  const cta = b.cta ? renderExternalButton(b.cta.label, b.cta.url, s) : "";
  return `${sectionOpen("hero", s)}${image}${textEl("h1", undefined, b.title, s)}${subtitle}${cta}</section>`;
}

function renderStory(b: LandingStoryBlock): string {
  const s = b.style;
  const heading = b.heading ? textEl("h2", undefined, b.heading, s) : "";
  return `${sectionOpen("story", s)}${heading}${paragraphsHtml(b.paragraphs, s)}</section>`;
}

function renderImpact(b: LandingImpactBlock): string {
  const s = b.style;
  const heading = b.heading ? textEl("h2", undefined, b.heading, s) : "";
  const valueAttr = s?.accentColor ? ` style="color: ${escapeAttr(s.accentColor)}"` : textColorAttr(s);
  const labelAttr = textColorAttr(s);
  const captionAttr = textColorAttr(s);
  const items = b.items
    .map(
      (it) =>
        `<div class="item"><span class="value"${valueAttr}>${escapeHtml(it.value)}</span><span class="label"${labelAttr}>${escapeHtml(it.label)}</span>${it.caption ? `<span class="caption"${captionAttr}>${escapeHtml(it.caption)}</span>` : ""}</div>`,
    )
    .join("");
  return `${sectionOpen(undefined, s)}${heading}<div class="impact">${items}</div></section>`;
}

function renderQuote(b: LandingQuoteBlock): string {
  const s = b.style;
  const attribution = b.attribution ? `<cite${textColorAttr(s)}>— ${escapeHtml(b.attribution)}</cite>` : "";
  return `${sectionOpen(undefined, s)}<blockquote${textColorAttr(s)}>${escapeHtml(b.text)}${attribution}</blockquote></section>`;
}

function renderCta(b: LandingCtaBlock): string {
  const s = b.style;
  const body = b.body ? textEl("p", undefined, b.body, s) : "";
  return `${sectionOpen("cta", s)}${textEl("h2", undefined, b.headline, s)}${body}${renderExternalButton(b.buttonLabel, b.buttonUrl, s)}</section>`;
}

function renderImage(b: LandingImageBlock): string {
  const s = b.style;
  const caption = b.caption ? `<figcaption${textColorAttr(s)}>${escapeHtml(b.caption)}</figcaption>` : "";
  return `${sectionOpen(undefined, s)}<figure><img src="${escapeAttr(b.src)}" alt="${escapeAttr(b.alt)}" />${caption}</figure></section>`;
}

function renderEmailLink(b: LandingEmailLinkBlock, ctx: LandingPageRenderContext): string {
  const s = b.style;
  const headline = b.headline ? textEl("h2", undefined, b.headline, s) : "";
  const body = b.body ? textEl("p", undefined, b.body, s) : "";
  const mailto = fundingAppealInboundEmailAddress(ctx.appealId, ctx.inboundEmailDomain);
  return `${sectionOpen("email-link", s)}${headline}${body}${renderExternalButton(b.linkLabel, `mailto:${mailto}`, s)}</section>`;
}

function renderSocialLinks(b: LandingSocialLinksBlock, ctx: LandingPageRenderContext): string {
  const selection = normalizeFundingClubSocialLinksSelection(b);
  const links = resolveFundingClubSocialLinks(ctx.clubSocialLinks, selection);
  if (links.length === 0) return "";
  const inner = renderFundingClubSocialLinksHtml(links, {
    directoryBaseUrl: ctx.clubSocialLinks?.directoryBaseUrl,
  });
  return `${sectionOpen("social-links", b.style)}${inner}</section>`;
}

function renderContactForm(b: LandingContactFormBlock, ctx: LandingPageRenderContext): string {
  const s = b.style;
  const headline = b.headline ? textEl("h2", undefined, b.headline, s) : "";
  const body = b.body ? textEl("p", undefined, b.body, s) : "";
  const submitLabel = b.submitLabel?.trim() || "Send message";
  const mountHtml = ctx.contactFormPreview
    ? `<div class="contact-form-mount">${renderFundingAppealContactFormHtml({ appealId: ctx.appealId, submitLabel, preview: true })}</div>`
    : (() => {
        const submitAttr = b.submitLabel ? ` data-submit-label="${escapeAttr(b.submitLabel)}"` : "";
        return `<div class="contact-form-mount" ${FUNDING_APPEAL_CONTACT_FORM_ATTR}="${escapeAttr(ctx.appealId)}"${submitAttr}></div>`;
      })();
  const attrs: string[] = [`id="${FUNDING_APPEAL_CONTACT_SECTION_ID}"`];
  if (s) {
    const decls = blockStyleDecls(s);
    if (decls) attrs.push(`style="${escapeAttr(decls)}"`);
  }
  return `<section class="contact-form" ${attrs.join(" ")}>${headline}${body}${mountHtml}</section>`;
}

function renderDonationTiers(b: LandingDonationTiersBlock, ctx: LandingPageRenderContext): string {
  const tiers = ctx.donationTiers ?? [];
  if (tiers.length === 0) return "";
  const s = b.style;
  const currency = (ctx.currency ?? "usd").trim() || "usd";
  const heading = b.heading ? textEl("h2", undefined, b.heading, s) : "";
  const body = b.body ? textEl("p", undefined, b.body, s) : "";
  const contactHref = `#${FUNDING_APPEAL_CONTACT_SECTION_ID}`;
  const cards = tiers
    .map((tier) => {
      const highlightClass = tier.highlight ? " tier-card highlight" : " tier-card";
      const amount = formatCurrencyCents(tier.amount_cents, currency);
      const cta = renderExternalButton(b.buttonLabel, contactHref, s);
      return `<div class="${highlightClass.trim()}"><span class="tier-amount">${escapeHtml(amount)}</span><span class="tier-label">${escapeHtml(tier.label)}</span><p class="tier-desc">${escapeHtml(tier.description)}</p>${cta}</div>`;
    })
    .join("");
  return `${sectionOpen("donation-tiers", s)}${heading}${body}<div class="tier-grid">${cards}</div></section>`;
}

function renderExternalButton(label: string, url: string, style: LandingBlockStyle | undefined): string {
  const decls: string[] = [];
  if (style?.accentColor) decls.push(`background-color: ${style.accentColor}`);
  if (style?.textColor) decls.push(`color: ${style.textColor}`);
  const styleAttr = decls.length ? ` style="${escapeAttr(decls.join("; "))}"` : "";
  return `<a class="button" href="${escapeAttr(url)}" target="_blank" rel="noopener noreferrer"${styleAttr}>${escapeHtml(label)}</a>`;
}

function renderBlock(b: LandingBlock, ctx: LandingPageRenderContext): string {
  switch (b.type) {
    case "hero":
      return renderHero(b, ctx);
    case "story":
      return renderStory(b);
    case "impact":
      return renderImpact(b);
    case "quote":
      return renderQuote(b);
    case "cta":
      return renderCta(b);
    case "image":
      return renderImage(b);
    case "email_link":
      return renderEmailLink(b, ctx);
    case "contact_form":
      return renderContactForm(b, ctx);
    case "social_links":
      return renderSocialLinks(b, ctx);
    case "donation_tiers":
      return renderDonationTiers(b, ctx);
  }
}

/**
 * Split a composed landing page document into body markup and CSS.
 * `setComponents` only ingests body HTML; styles in `<head>` must be applied via `setStyle`.
 */
export function splitFundingLandingPageHtmlDocument(source: string): {
  bodyHtml: string;
  css: string;
} {
  const trimmed = source.trim();
  if (!trimmed) return { bodyHtml: "", css: "" };
  const looksLikeDocument = /<html[\s>]/i.test(trimmed) || /<body[\s>]/i.test(trimmed);
  if (!looksLikeDocument) return { bodyHtml: trimmed, css: "" };

  const root = parse(trimmed, { comment: false });
  const css = root
    .querySelectorAll("style")
    .map((el) => el.textContent ?? "")
    .join("\n")
    .trim();
  const body = root.querySelector("body");
  const bodyHtml = (body ? body.innerHTML : trimmed).trim();
  return { bodyHtml, css };
}

export function renderLandingPageHtmlFromBlocks(
  blocks: LandingBlock[],
  context: LandingPageRenderContext,
): string {
  if (!context.appealId?.trim()) {
    throw new Error("renderLandingPageHtmlFromBlocks requires appealId in context");
  }
  const template = context.template ?? DEFAULT_LANDING_PAGE_TEMPLATE;
  const styleBlock = buildLandingPageStyles(context.pageStyle, template);
  let bodyMarkup: string;
  if (template === "minimal") {
    bodyMarkup = `${renderPageMainOpenTag(context.pageStyle)}${blocks.map((b) => renderBlock(b, context)).join("")}</main>`;
  } else {
    const { header, body, footer } = partitionLandingBlocks(blocks);
    const parts: string[] = [`<main class="page card">`];
    if (header.length > 0) parts.push(header.map((h) => renderHeroAppeal(h, context)).join(""));
    if (body.length > 0) {
      parts.push(`<div class="page-body">${body.map((b) => renderBlock(b, context)).join("")}</div>`);
    }
    if (footer.length > 0) {
      parts.push(`<div class="page-footer">${footer.map((b) => renderBlock(b, context)).join("")}</div>`);
    }
    parts.push("</main>");
    bodyMarkup = `${renderShellOpenTag(context.pageStyle)}${parts.join("")}</div>`;
  }
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>${styleBlock}</style></head><body>${bodyMarkup}</body></html>`;
  return resolveFundingLandingPageMarkers(html, {
    appealId: context.appealId,
    inboundEmailDomain: context.inboundEmailDomain,
  });
}
